import { type ApiResult } from "@de/ist.style_components_v3";

import * as generatedApi from "../api/generated";
import {
	type CreateUntisISTBackendConfigRequest,
	type CreateUntisMdmConfigRequest,
	type CreateUntisXschoolScheduleConfigRequest,
	type GetUntisMdmTaskResultsParams,
	type UntisMdmTaskItem,
	type UntisMdmTaskResultPage,
	type UpdateUntisISTBackendConfigRequest,
	type UpdateUntisMdmConfigRequest,
	type UpdateUntisXschoolScheduleConfigRequest,
} from "../api/generated/model";

export const getPlugins = (sort: string | undefined, limit: number | undefined, cursor: string | undefined) => {
	return generatedApi.getPlugins({ cursor, limit, sort });
};

export const getMyPlugins = (_sort: string | undefined) => {
	return generatedApi.getDashboard();
};

export const getConfigs = () => {
	return generatedApi.getConfigs();
};

export const getConfigDetail = (id: string) => {
	return generatedApi.getConfigDetail(id);
};

export const getUntisMdmConfig = (configId: string) => {
	return generatedApi.getUntisMdmConfig(configId);
};

export const getUntisMdmTaskResults = (taskId: string) => {
	return generatedApi.getUntisMdmTaskResults(taskId);
};

export type GetUntisMdmTaskResultsArgs = GetUntisMdmTaskResultsParams & { id: string };

export const getUntisMdmTaskResultsAsync = async (
	args: GetUntisMdmTaskResultsArgs | undefined,
	cursor: string | undefined,
): Promise<ApiResult<UntisMdmTaskItem>> => {
	return await getUntisMdmTaskResultsInternal({
		cursor: cursor,
		id: args?.id ?? "",
		identifier: args?.identifier,
		limit: args?.limit ?? 10,
		operation: args?.operation ?? "",
		sort: args?.sort ?? "",
		status: args?.status ?? "",
		type: args?.type ?? "",
	});
};

export const getUntisMdmTask = (taskId: string) => {
	return generatedApi.getUntisMdmTask(taskId);
};

export const getTaskLogs = (taskId: string) => {
	return generatedApi.getExecutionLogs(taskId);
};

export const getUiOptions = async () => {
	return await generatedApi.getUiOptions();
};

export const requestPluginRun = (configId: string, complete: boolean) => {
	return generatedApi.createConfigRequest(configId, { complete });
};

export const createConfig = (request: CreateUntisMdmConfigRequest) => {
	return generatedApi.createUntisMdmConfig(request);
};

export const updateConfig = (id: string, request: UpdateUntisMdmConfigRequest) => {
	return generatedApi.updateUntisMdmConfig(id, request);
};

export const deleteConfig = (id: string) => {
	return generatedApi.deleteUntisMdmConfig(id);
};

export const getTasks = (itemsPerPage: number, cursor: string | undefined) => {
	return generatedApi.getTasks({ cursor, limit: itemsPerPage });
};

export const getUntisXschoolScheduleConfig = (configId: string) => {
	return generatedApi.getUntisXschoolScheduleConfig(configId);
};

export const createUntisXschoolScheduleConfig = (request: CreateUntisXschoolScheduleConfigRequest) => {
	return generatedApi.createUntisXschoolScheduleConfig(request);
};

export const updateUntisXschoolScheduleConfig = (configId: string, request: UpdateUntisXschoolScheduleConfigRequest) => {
	return generatedApi.updateUntisXschoolScheduleConfig(configId, request);
};

export const deleteUntisXschoolScheduleConfig = (configId: string) => {
	return generatedApi.deleteUntisXschoolScheduleConfig(configId);
};

export const getUntisXschoolSubstitutionConfig = (configId: string) => {
	return generatedApi.getUntisXschoolSubstitutionConfig(configId);
};

export const createUntisXschoolSubstitutionConfig = (request: CreateUntisXschoolScheduleConfigRequest) => {
	return generatedApi.createUntisXschoolSubstitutionConfig(request);
};

export const updateUntisXschoolSubstitutionConfig = (configId: string, request: UpdateUntisXschoolScheduleConfigRequest) => {
	return generatedApi.updateUntisXschoolSubstitutionConfig(configId, request);
};

export const deleteUntisXschoolSubstitutionConfig = (configId: string) => {
	return generatedApi.deleteUntisXschoolSubstitutionConfig(configId);
};

export const getUntisISTBackendConfig = (configId: string) => {
	return generatedApi.getUntisISTBackendConfig(configId);
};

export const createUntisISTBackendConfig = (request: CreateUntisISTBackendConfigRequest) => {
	return generatedApi.createUntisISTBackendConfig(request);
};

export const updateUntisISTBackendConfig = (configId: string, request: UpdateUntisISTBackendConfigRequest) => {
	return generatedApi.updateUntisISTBackendConfig(configId, request);
};

export const deleteUntisISTBackendConfig = (configId: string) => {
	return generatedApi.deleteUntisISTBackendConfig(configId);
};

const convertApiResponse = (response: UntisMdmTaskResultPage): ApiResult<UntisMdmTaskItem> => {
	return {
		first: response.first,
		items: response.items,
		last: response.last ?? undefined,
		next: response.next ?? undefined,
		prev: response.prev ?? undefined,
		self: response.self,
		total: response.total ?? undefined,
	};
};

const getUntisMdmTaskResultsInternal = async (args: {
	cursor: string | undefined;
	id: string;
	identifier?: string;
	limit: number;
	operation?: string;
	sort: string;
	status?: string;
	type: string;
}): Promise<ApiResult<UntisMdmTaskItem>> => {
	if (args.cursor) {
		return convertApiResponse(
			await generatedApi.getUntisMdmTaskResults(args.id, {
				cursor: args.cursor,
				limit: args.limit,
				sort: args.sort,
			}),
		);
	}
	return convertApiResponse(
		await generatedApi.getUntisMdmTaskResults(args.id, {
			identifier: args.identifier,
			limit: args.limit,
			operation: args.operation,
			sort: args.sort,
			status: args.status,
			type: args.type,
		}),
	);
};
